import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useForm } from 'react-hook-form'
import Select from '../../../dpe-usco/components/Select/Select'
import Widget from '../../../dpe-usco/components/Widget/Widget'
import TabbedTable from '../../../components/TabbedTable/TabbedTable'
import TableStatus from '../../../components/subComponents/TableStatus/TableStatus'
import {
  generateYearsOptions,
  convertToSentenceCase
} from '../../../services/utils'
import { getSalesYear } from '../../../services/apiHelper'
import moment from 'moment'

const generateDeliveryMonths = year => {
  const startDate = year ? `${year}-09-01` : new Date().getFullYear() + '-09-01'
  const momentDate = moment(startDate)
  const _monthArray = []
  const _monthObj = {}
  for (let i = 0;i < 13;i++) {
    const newDate = momentDate.clone().add(i, 'month')
    const delivery_month = newDate.format('MMM-YY')
    const month = newDate.format('MMMM').toLowerCase()
    const year = newDate.format('YYYY')
    _monthArray.push({
      delivery_month,
      child_plan_acres: '0',
      child_plan_units: '0',
      available_acres: '0',
      available_units: '0'
    })
    _monthObj[delivery_month] = {
      month,
      year,
      child_plan_acres: '0',
      child_plan_units: '0',
      available_acres: '0',
      available_units: '0',
      premium: '0'
    }
  }
  return { _monthObj, _monthArray }
}

const yearsOptions = generateYearsOptions ? generateYearsOptions(new Date().getFullYear()-1, 11) : []
const { _monthObj, _monthArray } = generateDeliveryMonths()
const emptyArray = []

const pStyle = 'tal p0 ml10 mt0 mb0 fz15'
const COMMERCIAL_FEED = 'Commercial feed'

const DeliveryWidget = ({
  prevPremiumType,
  editable,
  returnData,
  contractType,
  initialAcres,
  initialUnits,
  initialYear,
  initialAph,
  initialMonthlyUnits,
  planState,
  company_id,
  prodPlanData,
  production_plan_id=''
}) => {
  const[premiumType, setPremiumType] = useState(prevPremiumType === 'acres' ? 'ac' : 'bu')
  const { handleSubmit } = useForm()
  const [remaining, setRemaining] = useState({
    remaining_child_acres: 0,
    remaining_child_units: 0,
    remaining_acres: 0,
    remaining_units: 0
  })
  const [silageInputs, setSilageInputs] = useState({ ..._monthObj })
  const [earlageInputs, setEarlageInputs] = useState({ ..._monthObj })
  const [highMoistureCornInputs, setHighMoistureCornInputs] = useState({
    ..._monthObj
  })
  const [grainInputs, setGrainInputs] = useState({ ..._monthObj })
  const [monthArray, setMonthArray] = useState([..._monthArray])
  const [totalChildAcres, setTotalChildAcres] = useState(0)
  const [totalChildUnits, setTotalChildUnits] = useState(0)
  const [totalAcres, setTotalAcres] = useState(0)
  const [totalUnits, setTotalUnits] = useState(0)
  const [aph, setAph] = useState(0)
  const [contractYear, setContractYear] = useState('')
  const [usedInitialYear, setUsedInitialYear] = useState(false)
  const [prodPlanInventory, setProdPlanInventory] = useState('')

  useEffect(() => {
    if(initialYear){
      setContractYear(initialYear)
    } else {
      getSalesYear().then(sales_year => {
        setContractYear(sales_year?.data?.sales_year)
      })
    }
  }, [initialYear])

  const getProductTypeState = useCallback(
    type => {
      const silage = {
        type: 'silage',
        state: silageInputs,
        setter: setSilageInputs
      }

      const earlage = {
        type: 'earlage',
        state: earlageInputs,
        setter: setEarlageInputs
      }

      const highMoistureCorn = {
        type: 'high-moisture',
        state: highMoistureCornInputs,
        setter: setHighMoistureCornInputs
      }
      const grain = {
        type: 'grain',
        state: grainInputs,
        setter: setGrainInputs
      }
      if (contractType === COMMERCIAL_FEED) {
        switch (type) {
          case 'SILAGE':
            return silage
          case 'EARLAGE':
            return earlage
          case 'HIGH MOISTURE CORN':
            return highMoistureCorn
          case 'GRAIN':
            return grain
          case 'ALL':
            return [silage, earlage, grain, highMoistureCorn]
          default:
            return null
        }
      } else {
        switch (type) {
          case 'GRAIN':
            return grain
          case 'ALL':
            return [grain]
          default:
            return null
        }
      }
    },
    [
      contractType,
      silageInputs,
      setSilageInputs,
      earlageInputs,
      setEarlageInputs,
      highMoistureCornInputs,
      setHighMoistureCornInputs,
      grainInputs,
      setGrainInputs
    ]
  )

  useEffect(() => {
    if (initialUnits) {
      setTotalUnits(initialUnits)
      setTotalChildUnits(initialUnits)
    }
  }, [initialUnits])

  useEffect(() => {
    if (initialAcres) {
      setTotalAcres(initialAcres)
      setTotalChildAcres(initialAcres)
    }
  }, [initialAcres])

  useEffect(() => {
    if (initialAph) {
      setAph(initialAph)
    }
  }, [initialAph])

  const prevContractYear = useRef()
  useEffect(() => {
    const handleSelectYear = option => {
      setContractYear(option.id)

      const allInputs = getProductTypeState('ALL')
      // This shouldn't delete all the user data
      allInputs.forEach(({ setter }) => {
        const { _monthObj } = generateDeliveryMonths(option.id)
        setter(_monthObj)
      })
      const { _monthArray } = generateDeliveryMonths(option.id)
      setMonthArray([..._monthArray])
    }
    if (initialYear && !usedInitialYear) {
      prevContractYear.current = initialYear
      setContractYear(initialYear)
      setUsedInitialYear(true)
      handleSelectYear({ id: initialYear })
    } else if (prevContractYear.current !== contractYear) {
      prevContractYear.current = contractYear
      handleSelectYear({ id: contractYear })
    }
  }, [initialYear, contractYear, getProductTypeState, usedInitialYear])

  const initialPass = useRef(true)
  useEffect(() => {
    if (
      initialMonthlyUnits &&
      initialMonthlyUnits.length &&
      contractType &&
      initialPass.current
    ) {
      const allInputs = getProductTypeState('ALL')
      allInputs.forEach(({ type, setter }) => {
        const initialType = initialMonthlyUnits.filter(unit => {
          return unit.end_product_type === type
        })
        if (initialType.length) {
          setter(prev => {
            const temp = { ...prev }
            for (let monthInput in temp) {
              initialType.forEach(unit => {
                if (
                  temp[monthInput].month === unit.month &&
                  temp[monthInput].year === unit.year
                ) {
                  temp[monthInput].child_plan_acres = unit.child_plan_acres
                  temp[monthInput].child_plan_units = unit.child_plan_units
                  temp[monthInput].available_acres = unit.available_acres
                  temp[monthInput].available_units = unit.available_units
                  temp[monthInput].premium = unit.premium
                }
              })
            }
            return temp
          })
        }
      })
      initialPass.current = false
    }
  }, [contractType, getProductTypeState, initialMonthlyUnits])

  const selectYear = option => {
    setContractYear(option.id)
  }

  const updateDeliveryMonth = async(e, month, attribute, tab) => {  
    const temp = { ...e }
    const setterFunction = getProductTypeState(tab).setter
    let availData
    let calculatedData
    if(attribute === 'child_plan_units'){
      availData = 'child_plan_acres'
      if(totalChildUnits==='0' || totalChildUnits===''){
        calculatedData = 0
      } else {
        calculatedData = Math.round(temp.target.value/aph)
      }
    }
    if(attribute === 'child_plan_acres'){
      availData = 'child_plan_units'
      calculatedData = Math.round(temp.target.value*aph)
    }
    if(attribute === 'available_units'){
      availData = 'available_acres'
      if(totalUnits==='0' || totalUnits===''){
        calculatedData = 0
      } else {
        calculatedData = Math.round(temp.target.value/aph)
      }
    } 
    if(attribute === 'available_acres'){
      availData = 'available_units'
      calculatedData = Math.round(temp.target.value*aph)
    }
    if(attribute === 'premium'){
      setterFunction(prevDeliveryMonths => {
        return {
          ...prevDeliveryMonths,
          [month]: {
            ...prevDeliveryMonths[month],
            [attribute]: temp.target.value
          }
        }
      })
    } else{
      setterFunction(prevDeliveryMonths => {
        return {
          ...prevDeliveryMonths,
          [month]: {
            ...prevDeliveryMonths[month],
            [attribute]: temp.target.value,
            [availData]:calculatedData.toString()
          }
        }
      })
    }
  }
  const tabGenerator = type => {
    const value = convertToSentenceCase(type)
    const upperType = type.toUpperCase()
    const { state } = getProductTypeState(upperType)
    return {
      value,
      data: [...monthArray],
      structure: {
        headers: planState.parent_production_plan_id && contractType === 'Fuel' ? 
          ['Delivery month', 'Parent Plan Bu.', 'Child Plan Acres', 'Child Plan Bu.', 'Available acres', 'Available Bu.', 'Premium'] : planState.parent_production_plan_id ? 
            ['Delivery month', 'Parent Plan Bu.', 'Child Plan Acres', 'Child Plan Bu.', 'Available acres', 'Available Bu.'] : contractType === 'Fuel' ? 
              ['Delivery month', 'Total acres', 'Total Bu.', 'Premium'] :
              ['Delivery month', 'Total acres', 'Total Bu.'],
        dataKeys: planState.parent_production_plan_id && contractType === 'Fuel' ? 
          ['delivery_month', 'parent_plan_bu', 'child_plan_acres', 'child_plan_units', 'available_acres', 'available_units', 'premium'] : planState.parent_production_plan_id ? ['delivery_month', 'parent_plan_bu', 'child_plan_acres', 'child_plan_units', 'available_acres', 'available_units'] : contractType === 'Fuel' ?
            ['delivery_month', 'available_acres', 'available_units', 'premium'] :
            ['delivery_month', 'available_acres', 'available_units'],
        rowFormat: {
          delivery_month: {
            overwriteStyles: true,
            customStyle: 'df aic flx1'
          },
          parent_plan_bu: {
            overwriteStyles: true,
            customStyle: 'flx1 mr15',
            allContent: true,
            format: rowContent => {
              let parentPlanBu = planState.parent_production_plan?.find(data => rowContent.delivery_month.substring(0, 3).toLowerCase().indexOf(data.month.substring(0, 3)) !== -1)?.available_units
              if(rowContent.delivery_month.substring(0, 3) === 'Sep' && (contractYear+'').substring(2) !== rowContent.delivery_month.substring(4)){
                parentPlanBu = 0
              }
              return (
                <input
                  aria-label="parent_plan_bu"
                  value={parentPlanBu ? parentPlanBu : 0}
                  disabled
                  className="parent_plan_bu c-darkwhite"
                />
              )
            }
          },
          child_plan_acres: {
            overwriteStyles: true,
            customStyle: 'flx1 mr15',
            allContent: true,
            format: rowContent => {
              if (!editable) {
                return (
                  <div className="ml10">
                    {state[rowContent.delivery_month]?.child_plan_acres || '0'}
                  </div>
                )
              }
              return (
                <input
                  aria-label="child_plan_acres"
                  type="number"
                  name={`${rowContent.delivery_month}_child_acres`}
                  value={state[rowContent.delivery_month]?.child_plan_acres!=='0'?state[rowContent.delivery_month]?.child_plan_acres:''}
                  placeholder={state[rowContent.delivery_month]?.child_plan_acres==='0'? 0 : null}
                  onChange={e =>
                    updateDeliveryMonth(
                      e,
                      rowContent.delivery_month,
                      'child_plan_acres',
                      upperType
                    )
                  }
                  onKeyPress={aphKeyPress}
                  // disabled
                  className="available_acre_class"
                />
              )
            }
          },
          child_plan_units: {
            overwriteStyles: true,
            customStyle: 'flx1 mr15',
            allContent: true,
            format: rowContent => {
              if (!editable) {
                return (
                  <div className="ml10">
                    {state[rowContent.delivery_month]?.child_plan_units || '0'}
                  </div>
                )
              }
              return (
                <input
                  type="number"
                  aria-label="child_plan_units"
                  name={`${rowContent.delivery_month}_child_units`}                  
                  value={state[rowContent.delivery_month]?.child_plan_units!=='0'?state[rowContent.delivery_month]?.child_plan_units:''}
                  placeholder={state[rowContent.delivery_month]?.child_plan_units==='0'? 0 : null}
                  onChange={e =>
                    updateDeliveryMonth(
                      e,
                      rowContent.delivery_month,
                      'child_plan_units',
                      upperType
                    )
                  }
                  onKeyPress={aphKeyPress}
                />
              )
            }
          }, 
          available_acres: {
            overwriteStyles: true,
            customStyle: 'flx1 mr15',
            allContent: true,
            format: rowContent => {
              if (!editable) {
                return (
                  <div className="ml10">
                    {state[rowContent.delivery_month]?.available_acres || '0'}
                  </div>
                )
              }
              return (
                <input
                  aria-label="available_acres"
                  type="number"
                  name={`${rowContent.delivery_month}_available_acres`}
                  value={state[rowContent.delivery_month]?.available_acres!=='0'?state[rowContent.delivery_month]?.available_acres:''}
                  placeholder={state[rowContent.delivery_month]?.available_acres==='0'? 0 : null}
                  onChange={e =>
                    updateDeliveryMonth(
                      e,
                      rowContent.delivery_month,
                      'available_acres',
                      upperType
                    )
                  }
                  onKeyPress={aphKeyPress}
                  // disabled
                  className="available_acre_class"
                />
              )
            }
          },
          available_units: {
            overwriteStyles: true,
            customStyle: 'flx1 mr15',
            allContent: true,
            format: rowContent => {
              if (!editable) {
                return (
                  <div className="ml10">
                    {state[rowContent.delivery_month]?.available_units || '0'}
                  </div>
                )
              }
              return (
                <input
                  type="number"
                  aria-label="available_units"
                  name={`${rowContent.delivery_month}_available_units`}                  
                  value={state[rowContent.delivery_month]?.available_units!=='0'?state[rowContent.delivery_month]?.available_units:''}
                  placeholder={state[rowContent.delivery_month]?.available_units==='0'? 0 : null}
                  onChange={e =>
                    updateDeliveryMonth(
                      e,
                      rowContent.delivery_month,
                      'available_units',
                      upperType
                    )
                  }
                  onKeyPress={aphKeyPress}
                />
              )
            }
          }, 
          premium: {
            overwriteStyles: true,
            customStyle: 'flx1 mr15',
            allContent: true,
            format: rowContent => {
              if (!editable) {
                return (
                  <div className="ml10">{'0'}</div>
                )
              }
              return (
                <div style={{ position:'relative' }}>
                  <span style={{ position: 'absolute', right: '4px', top: '8px' }}>$/{premiumType}</span>
                  <input
                    type="number"
                    aria-label="premium"
                    name={`${rowContent.delivery_month}_premium`}                  
                    value={state[rowContent.delivery_month]?.premium!=='0'?state[rowContent.delivery_month]?.premium:''}
                    placeholder={state[rowContent.delivery_month]?.premium==='0'? 0 : null}
                    onChange={e =>
                      updateDeliveryMonth(
                        e,
                        rowContent.delivery_month,
                        'premium',
                        upperType
                      )
                    }
                    onKeyPress={aphKeyPress}
                  />
                </div>
              )
            }
          }
        }
      }
    }
  }
  const generateTabs = () => {
    const grain = tabGenerator('grain')
    if (contractType === COMMERCIAL_FEED) {
      return [
        grain,
        tabGenerator('silage'),
        tabGenerator('earlage'),
        tabGenerator('high moisture corn')
      ]
    } else {
      return [grain]
    }
  }

  const tabs = generateTabs()

  const onSubmit = values => {
    returnData({ type: 'UPDATE_MONTHLY_UNITS', payload: values })
  }

  useEffect(() => {
    let remaining_child_acres = totalChildAcres
    let remaining_child_units = totalChildUnits
    let remaining_acres = totalAcres
    let remaining_units = totalUnits
    const allStates = getProductTypeState('ALL')    
    allStates.forEach(({ state }) => {
      for (let row in state) {
        remaining_child_acres -= state[row].child_plan_acres
        remaining_child_units -= state[row].child_plan_units
        remaining_acres -= state[row].available_acres
        remaining_units -= state[row].available_units
      }
    })
    const temp = { remaining_child_acres, remaining_child_units, remaining_acres, remaining_units }
    setRemaining(temp)
  }, [getProductTypeState, setRemaining, totalAcres, totalUnits, setGrainInputs, grainInputs, totalChildAcres, totalChildUnits])

  const previousProductInputs = useRef([])

  useEffect(() => {
    const returnArray = []
    const allStates = getProductTypeState('ALL')
    allStates.forEach(({ type, state }) => {
      for (let row in state) {
        if (!planState.parent_production_plan_id) {
          if (
            state[row].available_acres &&
              state[row].available_units &&
              state[row].available_acres !== '0' &&
              state[row].available_units !== '0'
          ) {
            const newObj = {
              month: state[row].month,
              year: state[row].year,
              end_product_type: type,
              available_acres: state[row].available_acres,
              available_units: state[row].available_units,
              premium: state[row].premium,
              premium_type: premiumType === 'ac' ? 'acres' : 'bushels'
            }
            returnArray.push(newObj)
          } else {
            returnArray.push({})
          }
        } else {
          if ((
            state[row].available_acres &&
            state[row].available_units &&
            state[row].available_acres !== '0' &&
            state[row].available_units !== '0'
          ) || (
            state[row].child_plan_acres &&
              state[row].child_plan_units &&
              state[row].child_plan_acres !== '0' &&
              state[row].child_plan_units !== '0'
          )) {
            const newObj = {
              month: state[row].month,
              year: state[row].year,
              end_product_type: type,
              available_acres: state[row].available_acres===null?'0':state[row].available_acres,
              available_units: state[row].available_units===null?'0':state[row].available_units,
              child_plan_acres: state[row].child_plan_acres===null?'0':state[row].child_plan_acres,
              child_plan_units: state[row].child_plan_units===null?'0':state[row].child_plan_units,
              premium: state[row].premium,
              premium_type: premiumType === 'ac' ? 'acres' : 'bushels'
            }
            returnArray.push(newObj)
          } else {
            returnArray.push({})
          }
        }
      }
    })
   
    if (
      returnArray.length ||
      (returnArray.length === 0 && previousProductInputs.current.length !== 0)
    ) {

      previousProductInputs.current = returnArray
      returnData({
        type: 'UPDATE_MONTHLY_UNITS',
        payload: returnArray.length ? returnArray : emptyArray
      })
    }
  }, [getProductTypeState, returnData, premiumType,planState.parent_production_plan_id])

  useEffect(() => {
    returnData({ type: 'UPDATE_TOTAL_CHILD_ACRES', payload: totalChildAcres })
  }, [returnData, totalChildAcres])

  useEffect(() => {
    returnData({ type: 'UPDATE_TOTAL_CHILD_UNITS', payload: totalChildUnits })
  }, [returnData, totalChildUnits])

  useEffect(() => {
    returnData({ type: 'UPDATE_TOTAL_ACRES', payload: totalAcres })
  }, [returnData, totalAcres])

  useEffect(() => {
    returnData({ type: 'UPDATE_TOTAL_UNITS', payload: totalUnits })
  }, [totalUnits, returnData])

  useEffect(() => {
    returnData({ type: 'UPDATE_CONTRACT_YEAR', payload: contractYear })
    returnData({ type: 'UPDATE_CONTRACT_YEAR_ERROR', payload: { contractYear, prodPlanData, production_plan_id } })
  }, [contractYear, returnData])

  useEffect(() => {
    returnData({ type: 'UPDATE_APH', payload: aph })
  }, [aph, returnData])

  const bottomRow = (
    <div className="bt1-s-lightgray df jcfe aic h44 pl16 pr16">
      <div className="flx1"></div>
      {planState.parent_production_plan_id ? 
        <div className="flx1 mr15"></div> : null }
      {planState.parent_production_plan_id ? 
        <div className="flx1 df jcfs mr15">
          {Math.round(remaining.remaining_child_acres) + ' remaining'} 
        </div> : null} 
      {planState.parent_production_plan_id ?
        <div className="flx1 df jcfs mr15">
          {Math.round(remaining.remaining_child_units) + ' remaining'}
        </div> : null}
      <div className="flx1 df jcfs mr15">
        {Math.round(remaining.remaining_acres)} remaining
      </div>
      <div className="flx1 df jcfs mr15">
        {Math.round(remaining.remaining_units)} remaining
      </div>
      {contractType === 'Fuel' ? <div className="flx1 mr15"></div> : null}
    </div>
  )
  const aphKeyPress = evt =>{
    
    if (evt.which < 46 || evt.which > 57 || evt.which === 47)
    {
      evt.preventDefault()
    }
  }
  const updateTotalAcresField=async e=>{
    let temp={ ...e }
    setTotalUnits(temp.target.value)
    setTotalChildUnits(temp.target.value)
    if(temp.target.value!==''){
      if(aph){
        let calculatedTotalAcres=Math.round(temp.target.value/aph)
        setTotalAcres(calculatedTotalAcres)
        setTotalChildAcres(calculatedTotalAcres)
      
      }
    }else{
      setTotalAcres(0)
      setTotalChildAcres(0)
    }
  }

  const updateTotalUnitsField=async e=>{
    let temp={ ...e }
    setTotalAcres(temp.target.value)
    setTotalChildAcres(temp.target.value)
    if(temp.target.value!==''){
      if(aph){
        let calculatedTotalUnits=Math.round(temp.target.value*aph)
        setTotalUnits(calculatedTotalUnits)
        setTotalChildUnits(calculatedTotalUnits)
      }
    }else{
      setTotalUnits(0)
      setTotalChildUnits(0)
    }
  }

  useEffect(()=>{
    setGrainInputs(prevDeliveryMonths => {
      Object.keys(prevDeliveryMonths).forEach(function(key){
        let unitsValue=prevDeliveryMonths[key]['available_units']
        if(unitsValue === 0 || unitsValue === ''){
          prevDeliveryMonths[key]['available_acres'] = '0'
        }else{
          let result=Math.round(unitsValue/aph)
          if(isFinite(result)){
            prevDeliveryMonths[key]['available_acres'] = result.toString()
          }else{
            prevDeliveryMonths[key]['available_acres'] = '0'
          }
           
        }    
      }) 
       
      return { ...prevDeliveryMonths }    
    })
     
  },[aph])

  useEffect(()=>{
    setGrainInputs(prevDeliveryMonths => {
      Object.keys(prevDeliveryMonths).forEach(function(key){
        let unitsValue=prevDeliveryMonths[key]['child_plan_units']
        if(unitsValue === 0 || unitsValue === ''){
          prevDeliveryMonths[key]['child_plan_acres'] = '0'
        }else{
          let result=Math.round(unitsValue/aph)
          if(isFinite(result)){
            prevDeliveryMonths[key]['child_plan_acres'] = result.toString()
          }else{
            prevDeliveryMonths[key]['child_plan_acres'] = '0'
          }
           
        }    
      }) 
       
      return { ...prevDeliveryMonths }    
    })
     
  },[aph])

  const aphOnChange= e=>{
  
    let temp = { ...e }
    setAph(temp.target.value)
    if(temp.target.value!==''){
      setAph(temp.target.value)
      if(totalUnits){
        let calculatedTotalAcres=Math.round(totalUnits/temp.target.value)
        if(isFinite(calculatedTotalAcres)){
          setTotalAcres(calculatedTotalAcres)
          setTotalChildAcres(calculatedTotalAcres)
        }else{
          setTotalAcres(0)
          setTotalChildAcres(0)
        }
      }
    } else{
      setTotalAcres(0)
      setTotalChildAcres(0)
    }
  }
  useEffect(() => {
    if(prodPlanInventory.length === 6){
      returnData({ type: 'UPDATE_PROD_PLAN_INVENTORY', payload: prodPlanInventory })
    } else if(prodPlanInventory.length === 0 && planState.inventory_code !== undefined){
      returnData({ type: 'UPDATE_PROD_PLAN_INVENTORY', payload: null })
    }  else if(prodPlanInventory.length < 6 && planState.inventory_code !== undefined){
      returnData({ type: 'UPDATE_PROD_PLAN_INVENTORY', payload: 'invalid_code' })
    } 
  }, [prodPlanInventory, returnData, planState.inventory_code])

  useEffect(() => {
    if(planState.company_inventory_code) {
      if(planState.company_inventory_code.length<6)
        setProdPlanInventory((''+contractYear).substring(2, 4) +''+ planState.company_inventory_code)
      else
        setProdPlanInventory(planState.company_inventory_code.replace(/^\d{2}/, (''+contractYear).substring(2, 4)))
    }
    if(company_id.toLowerCase() === 'syngenta') {
      setProdPlanInventory((''+contractYear).substring(2, 4) +'SYN1')
    }
  }, [planState.company_inventory_code, contractYear, planState, company_id])
  
  return (
    <Widget title="Delivery" dataTestId="deliveryWidget">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='mb10 w45%'>
          <label htmlFor='inventory' className="df mb10">Production plan inventory code</label>
          <input
            aria-label="prod_plan_inventory"
            id='prod_plan_inventory'
            className='bd2-s-input mt4 fz15 c-darkwhite' 
            type="text" 
            name="prod_plan_inventory"
            value={prodPlanInventory}
            maxLength={6}
            disabled
          />
        </div>
        <div className="df mb10 bb1-s-lightgray jcsb">
          <div className="w45%">
            <label className="df mb10" htmlFor="totalAcres">
              Total acres*
            </label>
            {!editable ? (
              <p className={`${pStyle} mb20`}>{initialAcres || '0'}</p>
            ) : (
              <input
                type="number"
                id="totalAcres"
                name="totalAcres"
                aria-label="totalAcres"
                value={totalAcres}
                onKeyPress={aphKeyPress}              
                onChange={updateTotalUnitsField}
              />
            )}
          </div>
          <div className="w45%">
            <label className="df mb10" htmlFor="totalUnits">
              Total Bushels*
            </label>
            {!editable ? (
              <p className={`${pStyle} mb20`}>{initialUnits || '0'}</p>
            ) : (
              //first
              <input
                type="number"
                className="mb20"
                id="totalUnits"
                name="totalUnits"
                aria-label="totalUnits"
                value={totalUnits}  
                onKeyPress={aphKeyPress}              
                onChange={updateTotalAcresField}
              />
            )}
          </div>
        </div>
        <div className="df mb10 bb1-s-lightgray jcsb">
          <div className="w45%">
            <label className="mb10" htmlFor="contractYear">
            Contract year*
            </label>
            {!editable ? (
              <p className={pStyle}>{initialYear || contractYear}</p>
            ) : (
              <Select
                placeholder="Select a year"
                defaultId={initialYear ? initialYear : contractYear}
                handleSelection={selectYear}
                options={yearsOptions}
                name="contractYear"
                id="contractYear"
              />
            )}
          </div>
          <div className="w45%">
            <label className="df" htmlFor="totalUnits">
              APH* (Bu/Acre)
            </label>
            {!editable ? (
              <p className={`${pStyle} mb20`}>{initialAph || '0'}</p>
            ) : (
              <input
                type="number"
                className="mb20"
                id="aph"
                name="aph"
                aria-label="aph"
                value={aph}
                required
                onKeyPress={aphKeyPress}
                onChange={aphOnChange}
              />
            )}
          </div>
        </div>
        <TabbedTable
          IsInputTable={false}
          editMode={false}
          tabs={tabs}
          showPremiumToggle={true && contractType === 'Fuel'}
          premiumType={premiumType}
          setPremiumType={setPremiumType}
          statusComponent={
            contractType ? null : (
              <TableStatus
                status={'EMPTY'}
                prompt="Please select a contract type"
              />
            )
          }
          bottomOfTable={bottomRow}
        />
      </form>
    </Widget>
  )
}

export default DeliveryWidget